import {Modal, Select} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {SuperAdminResponse} from 'src/api/ProactApiClient';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {SubmitButton} from 'src/design/molecules/SubmitButton';
import {useGetInstanceListQuery} from 'src/redux/queries/instance';
import {useChangePrimaryInstanceMutation} from 'src/redux/queries/userInstance';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {z} from 'zod';

const schema = z.object({
  targetInstanceId: z.string().min(1, 'Please select an instance.'),
});

type ChangePrimaryInstanceFormValues = z.infer<typeof schema>;

const defaultValues: ChangePrimaryInstanceFormValues = {
  targetInstanceId: '',
};

export interface ChangePrimaryInstanceModalProps {
  target: SuperAdminResponse | null;
  onClose: () => void;
  onChanged?: () => void;
}

export const ChangePrimaryInstanceModal: React.FC<
  ChangePrimaryInstanceModalProps
> = ({target, onClose, onChanged}) => {
  const {setAlert} = useAlert();
  const [changePrimaryInstance, {isLoading}] = useChangePrimaryInstanceMutation();

  const {data: instanceListData} = useGetInstanceListQuery(
    {start: 0, length: 1000},
    {skip: target === null}
  );

  const instanceOptions = useMemo(
    () =>
      (instanceListData?.data ?? [])
        .filter(inst => inst.id !== target?.primaryInstanceId)
        .map(inst => ({
          value: String(inst.id),
          label: inst.name,
        })),
    [instanceListData, target?.primaryInstanceId]
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
  } = useForm<ChangePrimaryInstanceFormValues>({
    mode: 'onSubmit',
    defaultValues,
    resolver: zodResolver(schema),
  });

  if (target === null) return null;

  const handleSubmit = async (values: ChangePrimaryInstanceFormValues) => {
    try {
      await changePrimaryInstance({
        accountId: target.accountId,
        targetInstanceId: Number(values.targetInstanceId),
      }).unwrap();
      setAlert(
        `${target.email}'s primary instance has been updated.`,
        'success'
      );
      reset();
      onChanged?.();
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      onClose();
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Change Primary Instance"
      size="md"
      styles={{title: {fontWeight: 700}}}>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Current primary instance:{' '}
            <strong>{target.primaryInstanceName ?? '—'}</strong>. Select a new
            primary instance below.
          </Text>
          <Controller
            name="targetInstanceId"
            control={control}
            render={({field, fieldState}) => (
              <Select
                label="New Primary Instance"
                placeholder="Select an instance"
                required
                data={instanceOptions}
                searchable
                value={field.value || null}
                onChange={val => field.onChange(val ?? '')}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          <Divider />
          <Group justify="flex-end">
            <DialogButton size="sm" onClick={onClose}>
              Cancel
            </DialogButton>
            <SubmitButton size="sm" loading={isLoading}>
              Change Primary
            </SubmitButton>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
